// CLIENT-ONLY: the browser-only provider stack (AppInsights, Auth0, token, promotion tracking,
// ThemeContext) wrapped around the shared ThemedApp. The server omits these and renders ThemedApp
// directly inside ServerAppTree.
import React, { useMemo, useRef, useState } from 'react';
import { createStore, GlobalState, StateMachineProvider } from 'little-state-machine';
import { PromotionTrackingContext } from '@lib/PromotionTrackingContext';
import { ThemeVariants } from '../../../../types/themes';
import { TokenProvider } from '../../hooks/useAuth0Token';
import { useGTMInitializer } from '../../hooks/GTM/useGTMInitializer';
import { VirtualPageViewTracker } from '../../hooks/VirtualPageViewTracking/useVirtualPageViewTracking';
import { useAnchors } from '../../hooks/useAnchors';
import { AppInsightsContextProvider } from '../../components/AppInsightsContextProvider';
import Auth0ProviderWithHistory from '../../components/Auth0ProviderWithHistory';
import { ThemeContextProvider } from '../../components/ThemeContextProvider';
import { ThemedApp } from './ThemedApp';

export type FormNames = {
  [key in keyof GlobalState]?: GlobalState[key];
};

createStore(
  {
    membershipForm: {},
    membersForm: {},
    householdMemberForm: {},
    internationalDrivingPermitForm: {},
    idpCustomerForm: {},
    idpDocumentForm: {},
    addonsForm: {},
  },
  {},
);

export const ThemedAppRoot = ({
  initialTheme,
  abTestWeight,
}: {
  initialTheme?: ThemeVariants | null;
  abTestWeight?: number;
}) => {
  useGTMInitializer();
  useAnchors();

  const [disablePromotionTracking, setDisablePromotionTracking] = useState(false);
  const [isVirtualPageViewTracked, setIsVirtualPageViewTracked] = useState(false);
  const isVirtualPageViewTrackedRef = useRef(false);
  const pageViewKeyRef = useRef<string | null>(null);
  const promotionTrackingValue = useMemo(
    () => ({
      disablePromotionTracking,
      setDisablePromotionTracking,
      isVirtualPageViewTracked,
      setIsVirtualPageViewTracked,
      isVirtualPageViewTrackedRef,
      pageViewKeyRef,
    }),
    [disablePromotionTracking, isVirtualPageViewTracked, isVirtualPageViewTrackedRef, pageViewKeyRef],
  );

  return (
    <AppInsightsContextProvider>
      <Auth0ProviderWithHistory>
        <TokenProvider>
          <PromotionTrackingContext.Provider value={promotionTrackingValue}>
            <VirtualPageViewTracker />
            <StateMachineProvider>
              <ThemeContextProvider>
                <ThemedApp initialTheme={initialTheme} abTestWeight={abTestWeight} />
              </ThemeContextProvider>
            </StateMachineProvider>
          </PromotionTrackingContext.Provider>
        </TokenProvider>
      </Auth0ProviderWithHistory>
    </AppInsightsContextProvider>
  );
};
